/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Axios, { Canceler } from "axios";
import getDisplayName from "react-display-name";
import { paramCase } from "change-case";

//Foundation libraries
import { useSite } from "../../_foundation/hooks/useSite";
import categoryService from "../../_foundation/apis/search/categories.service";
import { STORELOCATORACTIONS } from "../../_foundation/constants/common";
import { localStorageUtil } from "../../_foundation/utils/storageUtil";
import { useStoreLocatorValue } from "../../_foundation/context/store-locator-context";

//Custom libraries
import { headerConfig } from "./headerConstant";
import { TOP_CATEGORIES_DEPTH_LIMIT } from "../../configs/catalog";
import { MINICART_CONFIGS } from "../../configs/order";
import * as ROUTES from "../../constants/routes";
import ContentRecommendationWidget from "../commerce-widgets/content-recommendation-widget";
import MiniCart from "./MiniCart";
import LanguageToggle from "./LanguageToggle";
import MegaMenu from "./MegaMenu";
import ExpandedMenu from "./ExpandedMenu";
import { SearchBar } from "../widgets/search-bar";
import AccountPopperContent from "./AccountPopperContent";

//Redux
import { userNameSelector, loginStatusSelector } from "../../redux/selectors/user";
import { addressDetailsSelector } from "../../redux/selectors/account";
import { ORG_SWITCH_ACTION } from "../../redux/actions/organization";
import { CONTRACT_SWITCH_ACTION } from "../../redux/actions/contract";
import { LOGOUT_REQUESTED_ACTION } from "../../redux/actions/user";
import { UPDATE_CATEGORIES_STATE_ACTION } from "../../redux/actions/category";
import { SELLERS_GET_ACTION } from "../../redux/actions/sellers";
import { currentContractIdSelector } from "../../redux/selectors/contract";
import { successSelector } from "../../redux/selectors/success";
import { SuccessMessageReducerState } from "../../redux/reducers/reducerStateInterface";
//UI
import useMediaQuery from "@mui/material/useMediaQuery";
import { ClickAwayListener, useTheme } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StoreIcon from "@mui/icons-material/Store";
import { Hidden } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import styled from "@mui/styled-engine-sc";
import {
  StyledAccountPopper,
  StyledButton,
  StyledContainer,
  StyledHeaderActions,
  StyledTypography,
  StyledGrid,
  StyledLink,
  StyledBox,
  StyledList,
} from "@hcl-commerce-store-sdk/react-component";
import { StyledPaper } from "components/elements";
import { StyledSwipeableDrawer } from "components/elements";
import { useWinDimsInEM } from "../../_foundation/hooks/use-win-dims-in-em";
import { selectedSellersSelector, sellersSelector } from "../../redux/selectors/sellers";
import { SELLER_STORAGE_KEY } from "../../constants/common";
import { SET_SELLER_ACTION } from "../../redux/actions/sellers";
import { Sellers } from "./sellers";
import { StyledHeader } from "components/elements";
import { StoreLocatorButton } from "./store-locator-button";
import PromoHeader from "./PromoHeader";
import MegaMenuHeader from "./MegaMenuHeader";
import EspotGeneral from "components/custom/spot/SpotTemplate";
import { dimensions } from "themes";

const headerHeight = dimensions.header.height.desktop;
const mobileHeaderHeight = dimensions.header.height.mobile;

  const StyledHeaderElements = styled("header")`
  ${({ theme }) => `
    margin: 0px 0px;
    position: relative;
    width: auto;
    
    .header-container-all-mobile {
      position: relative;
    }

    .header-container-all-desktop {
      position: relative;
      padding: 0px 50px 0px 50px;
    }

    .header-actionsButton {
      position: relative;
      padding: 12px;
      background: none;

      &.header-actionsButton-languageToggle .MuiTypography-body1 {
        line-height: 26px;
      }

      img {
        height: auto;
        vertical-align: middle;
      }

      .MuiSvgIcon-root {
        vertical-align: middle;
      }
    }

    .ui-icons-header {
      color: #272C35 !important;
      width: 24px;
      height: 25px;
    }

    .ui-search-icon-header {
      color: #272C35 !important;
      width: 23px;
      height: 23px;
    }

    .ui-icons-header-desktop {
      color: #272C35 !important;
      width: 26px;
      height: 28px;
    }

    .ui-hamburger-menu-icons {
      color: #272C35 !important;
      width: 26px;
    }

    .header-branding-desktop {
      display: flex;
      justify-content: center;
      align-items: center;

      a {
        display: block;
        line-height: 1;
      }

      img {
        padding-left: 5px;
        width: 170px;
        height: auto;
      }
      
    }

    .header-branding-mobile {
      display: flex;
      justify-content: center;
      align-items: center;

      a {
        display: block;
        line-height: 1;
      }

      img {
        padding-left: 5px;
        width: 135px;
        height: auto;
      }
      
    }

    .top-categories-header {
      padding: 0;
      height: auto;
      width: auto;
      position: relative;
      z-index: 20;
  
      ${theme.breakpoints.down("md")} {
        height: ${mobileHeaderHeight}px;
      }
    }

    .menu-hamburger {
      position: relative;
      padding-left: 13px;
      padding-right: 9px;
      background: none;
      border: 0;
      margin: 0;

      display: flex;
      justify-content: center;
      align-items: center;

      img {
        height: auto;
      }
  
      &:hover {
        color: ${theme.palette.primary.dark};
        cursor: pointer;
      }
  
      .MuiSvgIcon-root {
        vertical-align: middle;
      }

    }

    .searchBar-container {
      padding-right: 15px;
    }

    `}
  `;


interface HeaderProps {
  loggedIn: boolean;
}

const Buttonify = ({ children, ...props }) => {
  const { testId } = props;
  return (
    <StyledButton testId={testId} className="header-actionsButton" variant="text" color="secondary" {...props}>
      <StyledHeaderActions>{children}</StyledHeaderActions>{" "}
    </StyledButton>
  );
};




const MarketplacePopper = ({ sellerConfig }) => {
  const btnRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const guestSellerPicker = "guestSellerPicker";
  const { t } = useTranslation();
  const { mySite } = useSite();
  const onClickAway = useCallback((e) => {
    if (e.target.localName === "body") {
      return;
    }
    setOpen(false);
  }, []);

  return !mySite?.isB2B && sellerConfig.showSellerList ? (
    <>
      <div ref={btnRef}>
        <Buttonify testId="header-mp" onClick={setOpen.bind(null, !open)}>
          <StyledBox display="flex" flexDirection="column" alignItems="center" flexWrap="wrap">



            <StoreIcon />



            <StyledTypography variant="body1" component="p">
              {t("sellers.mp")}
            </StyledTypography>
          </StyledBox>
        </Buttonify>
      </div>
      <StyledAccountPopper
        id={guestSellerPicker}
        open={open}
        anchorEl={btnRef.current}
        placement="bottom-end"
        modifiers={[
          { name: "flip", enabled: false },
          { name: "preventOverflow", enabled: true },
          { name: "hide", enabled: false },
        ]}
        className="account-popper">
        <ClickAwayListener onClickAway={onClickAway}>
          <StyledPaper className="vertical-padding-1 horizontal-padding-1">
            <StyledList disablePadding>
              <Sellers />
            </StyledList>
          </StyledPaper>
        </ClickAwayListener>
      </StyledAccountPopper>
    </>
  ) : null;
};

/**
 * Header component
 * displays Header, Mini Cart and Mega Menu
 * @param props
 */
const Header: React.FC<HeaderProps> = (props: any) => {
  const widgetName = getDisplayName(Header);
  const { w } = useWinDimsInEM();
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [showSearchBar, setShowSearchBar] = useState<boolean>(false);
  const [topCategories, setTopCategories] = useState<Array<any>>([]);
  const [myAccountPopperOpen, setMyAccountPopperOpen] = useState<boolean>(false);

  const myAccountElRef = useRef<HTMLButtonElement>(null);

  const [miniCartPopperOpen, setMiniCartPopperOpen] = useState<boolean>(false);
  const miniCartElRef = useRef<HTMLButtonElement>(null);

  const [languageTogglePopperOpen, setLanguageTogglePopperOpen] = useState<boolean>(false);

  const languageToggleElRef = useRef<HTMLButtonElement>(null);

  const { mySite } = useSite();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const addressDetails = useSelector(addressDetailsSelector);
  const userName = useSelector(userNameSelector);
  const firstName = addressDetails?.firstName ?? userName?.firstName;
  const lastName = addressDetails?.lastName ?? userName?.lastName;
  const contractId = useSelector(currentContractIdSelector);
  const success: SuccessMessageReducerState = useSelector(successSelector);
  const userLoggedIn = useSelector(loginStatusSelector);
  const selectedSellers = useSelector(selectedSellersSelector);
  const sellerConfig = useSelector(sellersSelector);
  const sellers = localStorageUtil.get(SELLER_STORAGE_KEY);

  const userPreviousLoggedIn = useRef();

  const isB2B = Boolean(mySite?.isB2B);
  const loggedIn = props.loggedIn;
  const isShoppingEnabled = !isB2B || (isB2B && loggedIn);

  const isMobile = !useMediaQuery(theme.breakpoints.up("sm"));
  const isTablet = !useMediaQuery(theme.breakpoints.up("md"));
  const isDesktop = !useMediaQuery(theme.breakpoints.up("lg"));
  

  const myAccountPopperId = "HEADER_MY_ACCOUNT_Popper";
  const miniCartPopperId = "HEADER_MINI_CART_Popper";
  const languageTogglePopperId = "HEADER_LANGUAGE_TOGGLE_Popper";
  const CancelToken = Axios.CancelToken;
  const cancels: Canceler[] = [];
  const payloadBase: any = {
    widget: widgetName,
    cancelToken: new CancelToken(function executor(c) {
      cancels.push(c);
    }),
  };
  const payload = {
    ...payloadBase,
  };
  const navHome = () => navigate(ROUTES.HOME);
  const storeLocatorDispach = useStoreLocatorValue().dispatch;

  const handleMyAccountClick = () => {
    setMyAccountPopperOpen(true);
    setMiniCartPopperOpen(false);
    setLanguageTogglePopperOpen(false);
  };
  const handleMyAccountPopperClose = () => setMyAccountPopperOpen(false);

  const handleMiniCartClick = () => {
    setMiniCartPopperOpen(true);
    setMyAccountPopperOpen(false);
    setLanguageTogglePopperOpen(false);

    setTimeout(() => {
      window.scrollTo(0, 0);
    });
    setTimeout(() => {
      if (miniCartElRef !== null && miniCartElRef.current !== null) {
        miniCartElRef.current.focus();
      }
    }, 100);
  };

  const handleMiniCartPopperClose = useCallback(() => setMiniCartPopperOpen(false), []);

  const handleLanguageToggleClick = () => {
    setLanguageTogglePopperOpen(true);
    setMiniCartPopperOpen(false);
    setMyAccountPopperOpen(false);
  };
  const handleLanguageTogglePopperClose = () => {
    setLanguageTogglePopperOpen(false);
  };

  const handleOrgChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.persist();
    event.preventDefault();
    const orgId = event.target.value;
    dispatch(
      ORG_SWITCH_ACTION({
        query: { activeOrgId: String(orgId) },
        callback: navHome,
        ...payload,
      })
    );
  };

  const handleContractChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.persist();
    event.preventDefault();
    const conId = event.target.value;
    dispatch(
      CONTRACT_SWITCH_ACTION({
        query: { contractId: String(conId) },
        callback: navHome,
        ...payloadBase,
      })
    );
  };

  const handleLogout = (event) => {
    event.preventDefault();
    const param: any = {
      ...payload,
    };
    dispatch(LOGOUT_REQUESTED_ACTION(param));
    storeLocatorDispach({ type: STORELOCATORACTIONS.RESET_STORE_SELECTOR });
  };

  useEffect(() => {
    if (!userLoggedIn && userPreviousLoggedIn.current) {
      setMyAccountPopperOpen(false);
      setMiniCartPopperOpen(false);
      navHome();
    }
    userPreviousLoggedIn.current = userLoggedIn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoggedIn]);

  useEffect(() => {
    if (mySite !== null && contractId !== undefined) {
      const storeID: string = mySite.storeID;
      const parameters: any = {
        storeId: storeID,
        depthAndLimit: TOP_CATEGORIES_DEPTH_LIMIT,
        query: {
          contractId: contractId,
        },
        ...payload,
      };
      categoryService
        .getV2CategoryResourcesUsingGET(parameters)
        .then((res) => {
          setTopCategories(res.data.contents);
          dispatch(UPDATE_CATEGORIES_STATE_ACTION(res.data.contents));
        })
        .catch((e) => {
          setTopCategories([]);
          dispatch(UPDATE_CATEGORIES_STATE_ACTION([]));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySite, contractId, language, selectedSellers]);

  useEffect(() => {
    const payloadBase: any = {
      widget: widgetName,
      cancelToken: new CancelToken(function executor(c) {
        cancels.push(c);
      }),
    };
    dispatch(SELLERS_GET_ACTION(payloadBase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (success && success.key) {
      if (MINICART_CONFIGS.itemAddSuccessMsgKeys.includes(success.key)) {
        handleMiniCartClick();
      }
    }
  }, [success]);

  useEffect(() => {
    //splice to empty array
    cancels.splice(0, cancels.length).forEach((cancel) => {
      cancel();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sellerConfig.initialized) {
      if (sellers?.length && !sellerConfig.showSellerList) {
        dispatch(SET_SELLER_ACTION({ sellers: null }));
      } else if (sellers?.length && !selectedSellers?.length) {
        dispatch(SET_SELLER_ACTION({ sellers }));
      }
    }
  }, [sellerConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const crwProps = useMemo(
    () => ({
      widget: {
        id: `header-${paramCase(headerConfig.espot.eSpotName)}`,
        widgetName: "content-recommendation-widget",
        name: headerConfig.espot.eSpotName,
        properties: {
          emsName: headerConfig.espot.eSpotName,
        },
      },
      page: { name: "" },
    }),
    //Content is language sensitive, so listen to translation change to render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const toggleSearchBar = () => setShowSearchBar(!showSearchBar);
  const turnOffSearchBar = () => setShowSearchBar(false);
  const turnOnSearchBar = () => setShowSearchBar(true);
  const toggleOpen = () => setOpen(!open);
  const turnOffOpen = () => setOpen(false);
  const turnOnOpen = () => setOpen(true);

  return (
    <>
      <PromoHeader />
      <StyledHeader>

        <StyledHeaderElements>

          {/* La pestaña de promoción se muestra sólo si la tienda está siendo vista en móvil.
            El componente PromoHeader es el que elemento correspondiente a la pestaña color
            azul ubicada en la parte superior del header. */}
        
            <StyledGrid container className={(isMobile)? "header-container-all-mobile" : "header-container-all-desktop" } justifyContent="space-between" alignItems="center">
              
              <StyledGrid item xs={(isTablet)? 0 : 3}>

                <StyledGrid className="header-topbarSection" container alignItems="center">
                  <StyledGrid item alignItems="center">
                    {/* Botón de menu ubicado al lado de extrema izquierda del header */}
                    <Hidden mdUp>
                      <button className="menu-hamburger" data-testid="menu-hamburger-element" onClick={toggleOpen}>
                        <img 
                        src="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/OnboardStorefrontAssetStore/images-site/icons/menu-svgrepo-com.svg"
                        className="ui-hamburger-menu-icons"
                        />
                      </button>
                    </Hidden>
                  </StyledGrid>

                  {/* Se muestra el logo de la tienda. Este se jala desde el Management Center, con el
                      componente "EspotGeneral" */}
                  {mySite != null && (
                      <StyledGrid item>
                          <div className={isTablet ? "header-branding-mobile" : "header-branding-desktop"}>
                            <EspotGeneral espotName="LogoHeader" />
                          </div>
                      </StyledGrid>
                  )}
                </StyledGrid>

              </StyledGrid>

              <StyledGrid item xs={(isTablet)? 0 : 0.5}></StyledGrid>

              <StyledGrid item xs>
                <Hidden mdDown>
                  <StyledGrid className="top-categories-header" container justifyContent="center" alignItems="center">
                      <ExpandedMenu pages={topCategories} />
                  </StyledGrid>
                </Hidden>
              </StyledGrid>
              
              <StyledGrid item xs={(isTablet)? 0 : 3.5}>
                <StyledGrid className="header-topbarSection" container justifyContent="flex-end" alignItems="center">
                  {sellerConfig?.showSellerList ? <MarketplacePopper {...{ sellerConfig }} /> : null}

                  <Hidden mdDown>
                    <StyledGrid item className="searchBar-container" data-testid="search-bar-desktop-largetablet-element">
                      <SearchBar
                        showSearchBar={showSearchBar}
                        closeSearchBar={turnOffSearchBar}
                        openSearchBar={turnOnSearchBar}
                      />
                    </StyledGrid>
                  </Hidden>

                  {/* El botón de login se muestra dependiendo de si el usuario está loggeado o no, o si el
                      la tienda se muestra en móvil/tableta o no */}
                  {loggedIn ? (
                    <StyledGrid item alignItems="center">
                      <StyledButton
                        ref={myAccountElRef}
                        variant="text"
                        color="secondary"
                        className="header-actionsButton"
                        onClick={handleMyAccountClick}
                        testId="header-action-account">
                        {isTablet ? (
                          <StyledHeaderActions>
                            {/* CAMBIAR ESTILO DE TABLETA, DE SER NECESARIO */}
                            {/* <PermIdentityOutlinedIcon className={isTablet ? "ui-icons-header-desktop" : "ui-icons-header"} /> */}
                            <img 
                              src="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/OnboardStorefrontAssetStore/images-site/icons/account-svgrepo-com.svg"
                              className={isTablet ? "ui-icons-header" : "ui-icons-header-desktop"}
                            />

                            {/* <StyledTypography variant="body1" component="p">
                              {isMobile ? (t("Header.Actions.Account")) : t("Header.Actions.YourAccount")}
                            </StyledTypography> */}
                          </StyledHeaderActions>
                        ) : (
                          <StyledBox className="welcome-text" display="flex" flexDirection="column">
                            {/* <StyledTypography variant="button" component="p" className="account-welcome">
                              {firstName
                                ? t("Header.Actions.WelcomeFirstname", {
                                    firstName,
                                  })
                                : t("Header.Actions.WelcomeNoFirstname", {
                                    lastName,
                                  })}
                            </StyledTypography>
                            
                            <StyledBox display="flex" flexDirection="row" alignItems="center" flexWrap="wrap">
                              <StyledTypography variant="body2">{t("Header.Actions.YourAccount")}</StyledTypography>
                              {myAccountPopperOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </StyledBox> */}
                            <img 
                              src="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/OnboardStorefrontAssetStore/images-site/icons/account-svgrepo-com.svg"
                              className={isTablet ? "ui-icons-header" : "ui-icons-header-desktop"}
                            />
                          </StyledBox>
                        )}
                      </StyledButton>
                      <StyledAccountPopper
                        id={myAccountPopperId}
                        open={myAccountPopperOpen}
                        anchorEl={myAccountElRef.current}
                        onClose={handleMyAccountPopperClose}
                        placement={w <= 40 ? "bottom" : "bottom-end"}
                        modifiers={[
                          {
                            name: "flip",
                            enabled: false,
                          },
                          {
                            name: "preventOverflow",
                            enabled: false,
                          },
                          {
                            name: "hide",
                            enabled: false,
                          },
                        ]}
                        style={{zIndex: "100"}}
                        className="account-popper">
                        <ClickAwayListener onClickAway={handleMyAccountPopperClose}>
                          <StyledPaper className="horizontal-padding-2">
                            <StyledTypography variant="body1" component="div">
                              <AccountPopperContent
                                handleClose={handleMyAccountPopperClose}
                                handleOrgChange={handleOrgChange}
                                handleContractChange={handleContractChange}
                                handleLogout={handleLogout}
                                isB2B={isB2B}
                                userName={{ firstName, lastName }}
                              />
                            </StyledTypography>
                          </StyledPaper>
                        </ClickAwayListener>
                      </StyledAccountPopper>
                    </StyledGrid>
                  ) : (
                    <StyledGrid item style={{ display: "flex", alignItems: "center" }}>
                      <StyledLink to={ROUTES.SIGNIN}>
                        <Buttonify testId="header-sign-in">
                          <img 
                            src="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/OnboardStorefrontAssetStore/images-site/icons/account-svgrepo-com.svg"
                            className={isTablet ? "ui-icons-header" : "ui-icons-header-desktop" }
                          />
                        </Buttonify>
                      </StyledLink>
                    </StyledGrid>
                  )}

                  {/* Aquí se crea el botón para la búsqueda en la tienda. Se usa "Buttonify" para crear un boton
                      que ejecute "toggeSearchBar" al ser presionado, además de contener "SearchIcon" como el icono
                      del botón */}
                  <Hidden mdUp>
                    <StyledGrid item data-testid="search-bar-mobile-element" alignItems="center">
                        <Buttonify
                          testId="header-mp"
                          onClick={toggleSearchBar}
                          className={`header-actionsButton ${showSearchBar && "active"}`}
                          variant="text"
                          color="primary">
                          <img 
                            src="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/OnboardStorefrontAssetStore/images-site/icons/search-alt-svgrepo-com-02.svg"
                            className={isTablet ? "ui-search-icon-header" : "ui-icons-header-desktop" }
                          />
                        </Buttonify>
                    </StyledGrid>
                  </Hidden>

                  {/* El elemento "MiniCart" contiene el botón de la bolsa de compras */}
                  <StyledGrid item>
                    <MiniCart
                      id={miniCartPopperId}
                      open={miniCartPopperOpen}
                      handleClick={handleMiniCartClick}
                      handleClose={handleMiniCartPopperClose}
                      ref={miniCartElRef}
                    />
                  </StyledGrid>
                </StyledGrid>
              </StyledGrid>
            </StyledGrid>

          {showSearchBar && (
            <Hidden mdUp>
              <StyledContainer className="bottom-padding-1">
                <SearchBar
                  showSearchBar={showSearchBar}
                  closeSearchBar={turnOffSearchBar}
                  openSearchBar={turnOnSearchBar}
                />
              </StyledContainer>
            </Hidden>
          )}

          <StyledSwipeableDrawer
            anchor={useMediaQuery(theme.breakpoints.up("sm")) ? "top" : "left"}
            open={open}
            onClose={turnOffOpen}
            onOpen={turnOnOpen}
            className="header-menu"
            data-testid="header-menu-drawer-element">
            <MegaMenuHeader/>
            <StyledContainer className="bottom-padding-1" style={{padding: "0px 0px 0px 16px"}}>
              <StyledGrid container spacing={2} className={"menu-container " + (open ? "open" : "")}>
                <MegaMenu menutitle={t("MegaMenu.Title")} pages={topCategories} closeMegaMenu={turnOffOpen}></MegaMenu>
              </StyledGrid>
            </StyledContainer>
          </StyledSwipeableDrawer>
        </StyledHeaderElements>
      </StyledHeader>
    </>
  );
};

export { Header };
